import { useEditor } from '@modules/editor';
import type { EditorOptions } from '@tiptap/core';
import { useEffect, useMemo } from 'react';
import { useConversationDraft } from '../state/ConversationDraft';

type Props = {
  editorConfig: Partial<EditorOptions>;
  conversationId: string;
};

export const useConversationEditor = ({
  editorConfig,
  conversationId,
}: Props) => {
  const { content, setContent } = useConversationDraft(conversationId);

  const config = useMemo(() => {
    const originalChatUploadFile = editorConfig.extensionConfig?.chatUploadFile;

    return {
      ...editorConfig,
      extensionConfig: {
        ...editorConfig.extensionConfig,
        chatUploadFile: async (file: File) => {
          const text = editor.getHTML();
          originalChatUploadFile?.(file);
          if (text) {
            setContent(conversationId, text);
          }
        },
      },
    };
  }, [editorConfig, conversationId, setContent]);

  const { editor, currentValue, editorLoaded } = useEditor(
    {
      ...config,
      content,
    },
    [],
  );

  useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(content);
  }, [editor, content]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore editor here so the desevent will be triger only when the conversationId changes
  useEffect(() => {
    if (!editor) return;
    return () => {
      setContent(conversationId, editor.getHTML());
    };
  }, [conversationId]);

  return { editor, currentValue, editorLoaded };
};
